import { PDFDocument } from "pdf-lib";
import { stores } from "../../../stores";
import fontkit from "@pdf-lib/fontkit";
import { IProjectExamTransfer } from "@repositories/model/transfer/IProjectExamTransfer";
import { rgb } from 'pdf-lib';


// SampleQuestionGenerator.ts

export class QuestionsPDFGenerator {
    private projectRepository = stores.projectStore.projectRepository;

    private imageMaxWidth = 500;

    // Instance function to generate rubric
    async generatePDF(projectCode: string) {
        const projectExamTransfers = await this.projectRepository.getProjectExamList(projectCode) as IProjectExamTransfer[];
    
        if (!projectExamTransfers) return;
    
        const pdfDoc = await PDFDocument.create();
        pdfDoc.registerFontkit(fontkit);
    
        const fontBytes = await fetch('/font/NanumGothic.ttf').then(res => res.arrayBuffer());
        const font = await pdfDoc.embedFont(fontBytes);
    
        const boldFontBytes = await fetch('/font/NanumGothicBold.ttf').then(res => res.arrayBuffer());
        const boldFont = await pdfDoc.embedFont(boldFontBytes);
    
        const fontSize = 13;
        const smallFontSize = 11;
        const bigFontSize = 16;
        const pageMargin = 50;
        const defaultMargin = 20;
    
        let page = pdfDoc.addPage([595.28, 841.89]);
        let y = page.getHeight() - pageMargin;
        const maxWidth = page.getWidth() - pageMargin * 2;
    
        const sortedExamTransfers = [...projectExamTransfers].sort((a, b) => a.order - b.order);
    
        for (let examIndex = 0; examIndex < sortedExamTransfers.length; examIndex++) {
            const transfer = sortedExamTransfers[examIndex];
    
            // === Directive ===
            if (transfer.directive?.length) {

                const numLabel = `${examIndex + 1}. `;
                const directiveWithNumLabel = numLabel + transfer.directive;
                
                const directiveResult = await this.drawWrappedTextWithPageBreak(
                    pdfDoc, page, directiveWithNumLabel, pageMargin, y,
                    boldFont, bigFontSize, null, maxWidth, pageMargin
                );
                page = directiveResult.page;
                y = directiveResult.y - defaultMargin;
            }
    
            // === Paragraph ===
            if (transfer.paragraph?.length) {
                const mergedParagraph = transfer.paragraph;
                const marginFromDirective = 10;
                y = y - marginFromDirective;
                const paragraphResult = await this.drawWrappedTextWithPageBreak(
                    pdfDoc, page, mergedParagraph, pageMargin, y,
                    font, smallFontSize, null, maxWidth, pageMargin, undefined, true
                );
                page = paragraphResult.page;
                y = paragraphResult.y - defaultMargin;
                
                /*
                page.drawRectangle({
                    x: pageMargin - 10,
                    y: y + defaultMargin,
                    width: maxWidth + defaultMargin,
                    height: actualHeight,
                    borderColor: rgb(0.0, 0.0, 0.0),
                    borderWidth: 1.2,
                });
                */

                for (const span of paragraphResult.pageSpans || []) {
                    span.page.drawRectangle({
                      x: pageMargin - 10,
                      y: span.bottomY + defaultMargin/2.0,
                      width: maxWidth + defaultMargin,
                      height: span.topY - span.bottomY + marginFromDirective/2.0,
                      borderColor: rgb(0.0, 0.0, 0.0),
                      borderWidth: 1.2,
                    });
                  }
            }
    
            // === Questions ===
            const sortedQuestions = [...transfer.questions].sort((a, b) => a.order - b.order);
            const questionCount = sortedQuestions.length;
    
            for (let questionIndex = 0; questionIndex < questionCount; questionIndex++) {
                const question = sortedQuestions[questionIndex];
                const mergedQuestion = question.question;

                y = this.drawQuestionNumber(
                    page, examIndex, questionIndex, questionCount,
                    pageMargin, y, boldFont, fontSize
                );
    
                const questionResult = await this.drawWrappedTextWithPageBreak(
                    pdfDoc, page, mergedQuestion, pageMargin, y,
                    font, fontSize, boldFont, maxWidth, pageMargin, question.score
                );
                page = questionResult.page;
                y = questionResult.y - defaultMargin;
            }
    
            y -= defaultMargin;
        }
    
        const pdfBytes = await pdfDoc.save();
        const blob = new Blob([pdfBytes as BlobPart], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'questions.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }
    
    

    private drawQuestionNumber(page: any, examIndex: number, questionIndex: number, questionCount: number, x: number,y: number,font: any,fontSize: number): number {
        const label = questionCount === 1
		? `문항 ${examIndex + 1}`
		: `문항 ${examIndex + 1}-${questionIndex + 1}`;

        page.drawText(label, {
            x,
            y,
            font,
            size: fontSize,
            color: rgb(0, 0, 0),
        });
        return y - fontSize - 5; // Return new y-position
    }

    // Helper function to fetch an image as a Uint8Array
    private async fetchImageAsUint8Array(url: string): Promise<Uint8Array> {
        const response = await fetch(url);
        const blob = await response.blob();
        return new Uint8Array(await blob.arrayBuffer());
    }

    ///
    private async drawWrappedTextWithPageBreak(
        pdfDoc: any,
        page: any,
        htmlString: string,
        x: number,
        y: number,
        font: any,
        fontSize: number,
        boldFont: any,
        maxWidth: number,
        pageMargin: number,
        score?: number,
        trackBoxes: boolean = false
    ): Promise<{ page: any, y: number, pageSpans?: { page: any, topY: number, bottomY: number }[] }> {
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlString, "text/html");
        const childNodes = Array.from(doc.body.childNodes);
    
        const lineHeight = fontSize + 5;
        const pages: any[] = [page];
        const pageSpans: { page: any, topY: number, bottomY: number }[] = [];
        let currentTopY = y;
        let currentXOffset = 0;
    
        const drawTextBlock = async (
            text: string,
            initialXOffset: number,
            options: { font: any; color: { r: number, g: number, b: number }; indentX?: number; }
        ): Promise<number> => {
            let xOffset = initialXOffset;
            let localY = y;
        
            const safeText = text.replace(/-/g, '- ');
            const segments = safeText.split(/(\s+)/).filter(Boolean);
        
            for (const segment of segments) {
                const segmentWidth = options.font.widthOfTextAtSize(segment, fontSize);
        
                // ✅ If the whole segment is too wide, break it char-by-char
                if (segmentWidth > maxWidth) {
                    for (const char of segment) {
                        const charWidth = options.font.widthOfTextAtSize(char, fontSize);
        
                        if ((options.indentX ?? x) + xOffset + charWidth > x + maxWidth) {
                            localY -= lineHeight;
                            xOffset = 0;
    
                        }

                        if (localY - lineHeight < pageMargin) {
                            if (trackBoxes) {
                                pageSpans.push({ page, topY: currentTopY, bottomY: localY });
                                currentTopY = page.getHeight() - pageMargin;
                            }
                            page = pdfDoc.addPage([595.28, 841.89]);
                            localY = page.getHeight() - pageMargin;
                            pages.push(page);
                        }
        
                        const safeChar = char.replace(/\u2219/g, '•'); // Replace all ∙ with •

                        page.drawText(safeChar, {
                            x: (options.indentX ?? x) + xOffset,
                            y: localY,
                            size: fontSize,
                            font: options.font,
                            color: rgb(options.color.r, options.color.g, options.color.b),
                        });
        
                        xOffset += charWidth;
                    }
                } else {
                    // Normal wrapping for regular segments
                    if ((options.indentX ?? x) + xOffset + segmentWidth > x + maxWidth) {
                        localY -= lineHeight;
                        xOffset = 0;        
                    }

                    if (localY - lineHeight < pageMargin) {
                        if (trackBoxes) {
                            pageSpans.push({ page, topY: currentTopY, bottomY: localY });
                            currentTopY = page.getHeight() - pageMargin;
                        }
                        page = pdfDoc.addPage([595.28, 841.89]);
                        localY = page.getHeight() - pageMargin;
                        pages.push(page);
                    }

                    const safeSegment = segment.replace(/\u2219/g, '•'); // Replace all ∙ with •
        
                    page.drawText(safeSegment, {
                        x: (options.indentX ?? x) + xOffset,
                        y: localY,
                        size: fontSize,
                        font: options.font,
                        color: rgb(options.color.r, options.color.g, options.color.b),
                    });
        
                    xOffset += segmentWidth;
                }
            }
        
            y = localY;
            return xOffset;
        };
        
        
        const resolveStyle = (el: HTMLElement | null): { font: any, color: { r: number, g: number, b: number } } => {
            let useBold = false;
            let currentColor = { r: 0, g: 0, b: 0 }; // default: black
        
            let current = el;
        
            while (current) {
                const tag = current.tagName;
        
                if (tag === "STRONG" || tag === "B") useBold = true;
                // Note: italic styling is not currently used in PDF generation
        
                const style = current.getAttribute("style");

                if (style) {
                    // Match rgb(r, g, b)
                    const rgbMatch = style.match(/color:\s*rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
                    if (rgbMatch) {
                        currentColor = {
                            r: parseInt(rgbMatch[1]) / 255,
                            g: parseInt(rgbMatch[2]) / 255,
                            b: parseInt(rgbMatch[3]) / 255,
                        };
                    } else {
                        // fallback: match hex color
                        const hexMatch = style.match(/color:\s*#?([0-9a-fA-F]{6})/);
                        if (hexMatch) {
                            const hex = hexMatch[1];
                            currentColor = {
                                r: parseInt(hex.slice(0, 2), 16) / 255,
                                g: parseInt(hex.slice(2, 4), 16) / 255,
                                b: parseInt(hex.slice(4, 6), 16) / 255,
                            };
                        }
                    }
                }

        
                current = current.parentElement;
            }
        
            let selectedFont = font;
            if (useBold) selectedFont = boldFont ?? font;
        
            return { font: selectedFont, color: currentColor };
        };
        
    
        const isBlockElement = (el: Node): boolean => {
            return el instanceof HTMLElement && ["P", "DIV", "BLOCKQUOTE"].includes(el.tagName);
        };
    
        const processElement = async (el: Node, indent: number = 0) => {
            if (el.nodeType === Node.TEXT_NODE) {
                const text = el.textContent?.replace(/\u00A0/g, ' ');
                if (text && text.trim()) {
                    const style = resolveStyle(el.parentElement as HTMLElement);
                    currentXOffset = await drawTextBlock(text, currentXOffset, {
                        ...style,
                        indentX: x + indent,
                    });
                }

            } else if (el instanceof HTMLElement) {
                switch (el.tagName) {
                    case "SPAN":
                        for (const child of Array.from(el.childNodes)) {
                            await processElement(child, indent);
                        }
                        break;
                    case "BR":
                        y -= lineHeight;
                        currentXOffset = 0;
                        break;
                    case "UL":
                        for (const li of Array.from(el.children).filter(c => c.tagName === "LI")) {
                            await processElement(li, indent + 10);
                        }
                        break;

                    case "OL":
                        let index = 1;
                        for (const li of Array.from(el.children).filter(c => c.tagName === "LI")) {
                            li.setAttribute('data-index', index.toString());
                            await processElement(li, indent + 10);
                            index++;
                        }
                        break;

                    case "LI":
                        const prefix = el.parentElement?.tagName === "OL"
                            ? `${el.getAttribute("data-index")}. `
                            : "• ";

                        for (const child of Array.from(el.childNodes)) {
                            if (child.nodeType === Node.TEXT_NODE || (child instanceof HTMLElement && child.tagName === "P")) {
                                const style = resolveStyle(child as HTMLElement);
                                const text = child.textContent?.replace(/\u00A0/g, ' ');
                                if (text && text.trim()) {
                                    currentXOffset = await drawTextBlock(prefix + text.trim(), 0, {
                                        ...style,
                                        indentX: x + indent,
                                    });
                                    y -= lineHeight;
                                    currentXOffset = 0;
                                }
                            } else {
                                await processElement(child, indent);
                            }
                        }
                        break;
                    case "IMG":
                        const src = el.getAttribute("src");
                        if (src) {
                            const image = await this.embedImageForPage(src, pdfDoc);
                            if (!image) return;
                            const { width, height } = image;
    
                            if (y - height - 2 < pageMargin) {
                                if (trackBoxes) {
                                    pageSpans.push({ page, topY: currentTopY, bottomY: y });
                                    currentTopY = page.getHeight() - pageMargin;
                                }
                                page = pdfDoc.addPage([595.28, 841.89]);
                                y = page.getHeight() - pageMargin;
                                pages.push(page);
                            }
    
                            page.drawImage(image.image, {
                                x: x + indent,
                                y: y - height - 2,
                                width,
                                height,
                            });
                            y -= height + 17;
                            currentXOffset = 0;
                        }
                        break;
                    case "BLOCKQUOTE":
                        const blockTopY = y;
                        const innerIndent = indent + 20;
                        for (const child of Array.from(el.childNodes)) {
                            if (isBlockElement(child)) {
                                y -= lineHeight;
                                currentXOffset = 0;
                            }
    
                            await processElement(child, innerIndent);
    
                            if (isBlockElement(child)) {
                                y -= lineHeight;
                                currentXOffset = 0;
                            }
                        }
                        page.drawLine({
                            start: { x: x + 3, y: blockTopY },
                            end: { x: x + 3, y: y + 5},
                            thickness: 3,
                            color: rgb(0.8, 0.8, 0.8),
                        });
                        break;
                    case "DIV":
                        if (el.getAttribute("contenteditable") === "false" && el.querySelector("hr")) {
                            y -= lineHeight / 2;
                            page.drawLine({
                                start: { x: x, y: y + 10 },
                                end: { x: x + maxWidth, y: y + 10 },
                                thickness: 0.5,
                                color: rgb(0.8, 0.8, 0.8),
                            });
                            y -= lineHeight / 2;
                            currentXOffset = 0;
                            break;
                        }
                        // fallthrough to default
                    default:
                        for (const child of Array.from(el.childNodes)) {
                            await processElement(child, indent);
                        }
                        break;
                }
            }
        };
    
        for (const node of childNodes) {
            if (node instanceof HTMLElement && isBlockElement(node)) {
                if (currentXOffset !== 0) {
                    y -= lineHeight;
                    currentXOffset = 0;
                }
            }
    
            await processElement(node, 0);
    
            if (node instanceof HTMLElement && isBlockElement(node)) {
                y -= lineHeight;
                currentXOffset = 0;
            }
        }
    
        if (score !== undefined) {
            const scoreLabel = `(${score}점)`;
            if (y - lineHeight < pageMargin) {
                if (trackBoxes) {
                    pageSpans.push({ page, topY: currentTopY, bottomY: y });
                    currentTopY = page.getHeight() - pageMargin;
                }
                page = pdfDoc.addPage([595.28, 841.89]);
                y = page.getHeight() - pageMargin;
            }
            page.drawText(scoreLabel, {
                x,
                y,
                size: fontSize,
                font,
                color: rgb(0.5, 0.5, 0.5),
            });
            y -= lineHeight;
        }
    
        if (trackBoxes) {
            pageSpans.push({ page, topY: currentTopY, bottomY: y });
        }
    
        return { page, y, pageSpans: trackBoxes ? pageSpans : undefined };
    }
    
    
    // Helper for wrapping text
    private wrapTextIntoLines(
        text: string,
        font: any,
        fontSize: number,
        maxWidth: number,
        xOffset: number = 0
      ): { lines: string[]; lastLineWidth: number } {
        const words = text.replace(/\u00A0/g, " ").split(" ");
        const lines: string[] = [];
        let line = "";
        let isFirstLine = true;
        let lastLineWidth = 0;
      
        for (let word of words) {
          // If word is too long for even a single line, split it
          while (font.widthOfTextAtSize(word, fontSize) > maxWidth && word.length > 1) {
            let part = "";
            let i = 0;
            const availableWidth = isFirstLine ? maxWidth - xOffset : maxWidth;
      
            while (
              i < word.length &&
              font.widthOfTextAtSize(part + word[i], fontSize) <= availableWidth
            ) {
              part += word[i];
              i++;
            }
      
            if (line) {
              lines.push(line);
              line = "";
              isFirstLine = false;
            }
      
            if (part) {
              lines.push(part);
              word = word.slice(i);
            } else {
              lines.push(word[0]);
              word = word.slice(1);
            }
          }
      
          const availableWidth = isFirstLine ? maxWidth - xOffset : maxWidth;
          const testLine = line ? `${line} ${word}` : word;
      
          if (font.widthOfTextAtSize(testLine, fontSize) <= availableWidth) {
            line = testLine;
          } else {
            if (line) lines.push(line);
            line = word;
            isFirstLine = false;
          }
        }
      
        if (line) {
          lines.push(line);
          lastLineWidth = font.widthOfTextAtSize(line, fontSize);
        } else if (lines.length > 0) {
          const lastLine = lines[lines.length - 1];
          lastLineWidth = font.widthOfTextAtSize(lastLine, fontSize);
        }
      
        return { lines, lastLineWidth };
      }
    
    
    // Helper for image embedding and scaling
    private async embedImageForPage(imageUrl: string, pdfDoc: any): Promise<{ image: any, width: number, height: number } | null> {
        const extension = imageUrl.split('.').pop()?.toLowerCase() || "";
        const imageBytes = await this.fetchImageAsUint8Array(imageUrl);
        let image;
    
        if (extension === "jpg" || extension === "jpeg") {
            image = await pdfDoc.embedJpg(imageBytes);
        } else if (extension === "png") {
            image = await pdfDoc.embedPng(imageBytes);
        } else {
            console.warn("Unsupported image extension:", extension);
            return null;
        }
    
        const originalSize = image.scale(1);
        const maxW = this.imageMaxWidth;
        const scale = Math.min(maxW / originalSize.width, maxW / originalSize.height, 1);
        const scaled = image.scale(scale);
    
        return { image, width: scaled.width, height: scaled.height };
    }
}
